import React, { useCallback, useEffect, useState } from 'react'
import { useSelector } from 'react-redux'

import {
  BudgetAllocationsCheck,
  BudgetChartBudget,
  BudgetChartBudgets,
  BudgetChartTicket,
  Id,
  Request,
  RequestStandardSharing
} from 'src/types'

import budgetChartService from 'src/services/api/budget-graph'
import { ApplicationState } from 'src/store'
import { SearchType } from 'src/core/store/ducks/air/types'
import useReasonPolicyRule from 'src/core/hooks/use-reason-policy-rule'

import { StyledCard } from './styles'
import Budget from '..'

interface Props {
  request: Request
}

function budgetTicketAirReduce(
  fareKeys: string[]
): (
  previousValue: BudgetChartTicket[],
  currentValue: SearchType
) => BudgetChartTicket[] {
  return (accSearch, { list }) => [
    ...accSearch,
    ...list.reduce((accList, { request_quote_airs }) => {
      if (accList.length >= fareKeys.length) {
        return accList
      }
      return [
        ...accList,
        ...request_quote_airs.reduce(
          (accQuotes, { request_quote_air_fares }) => {
            if (accQuotes.length > 0) {
              return accQuotes
            }
            const foundFare = request_quote_air_fares.find(
              ({ request_quote_fare_hash }) =>
                fareKeys.includes(request_quote_fare_hash)
            )
            if (!foundFare) {
              return accQuotes
            }
            const ticket: BudgetChartTicket = {
              standard_type: 'AIR',
              total_value: foundFare.total_value
            }
            return [...accQuotes, ticket]
          },
          []
        )
      ]
    }, [])
  ]
}

async function getBudgetAllocationsCheck(
  budget_allocations: BudgetAllocationsCheck
) {
  const response = await budgetChartService.getBudgetAllocationsCheck(
    budget_allocations
  )

  return response.status === 200
    ? response.data.budgets.map((budget, index) => ({
        ...budget,
        key: index
      })) || []
    : []
}

async function getBudgetAllocationsRequest(request_id: Id) {
  const response = await budgetChartService.getBudgetAllocationsRequest(
    request_id
  )

  return response.status === 200
    ? response.data.budgets.map((budget, index) => ({
        ...budget,
        key: index
      })) || []
    : []
}

/**
 * Reduce function for request_standard_shares
 * @param acc
 * @param param1 CostCenters
 * @returns BudgetChartBudget[]
 */
function requestStandardSharingsReduce(
  acc: BudgetChartBudget[],
  standardSharing: RequestStandardSharing
): BudgetChartBudget[] {
  const { cost_center, percentage } = standardSharing
  if (!cost_center) {
    return acc
  }

  if (cost_center.budgets?.length) {
    return [
      ...acc,
      ...cost_center.budgets.map(({ budget_id, share }) => ({
        share,
        budget_id,
        cost_center_id: cost_center.cost_center_id
      }))
    ]
  }

  return [
    ...acc,
    {
      cost_center_id: cost_center.cost_center_id,
      share: percentage
    }
  ]
}

function BudgetSummary({ request }: Props) {
  const { air, hotels, vehicles } = useSelector((state: ApplicationState) => ({
    air: state.air,
    hotels: state.hotels,
    vehicles: state.vehicles
  }))

  const [barCharts, setBarCharts] = useState<BudgetChartBudgets[]>([])

  const policyRule = useReasonPolicyRule(request.request_user_data[0].reason_id)

  const getBudgetTicket = useCallback(() => {
    const tickets: BudgetChartTicket[] = []
    if (typeof request.request_id !== 'string') {
      return tickets
    }

    const airUsers = request.request_user_data.length
    const airFareHash = request.full_request_air.reduce<string[]>(
      (acc, { request_quote_air_fare_hash }) => [
        ...acc,
        ...request_quote_air_fare_hash
      ],
      []
    )

    const airTickets = Object.values(air.searches).reduce<BudgetChartTicket[]>(
      budgetTicketAirReduce(airFareHash),
      []
    )

    const hotelFareHash = request.full_request_hotel.reduce<string[]>(
      (acc, { rooms }) => [
        ...acc,
        ...rooms.map(
          ({ request_quote_hotel_room_hash }) => request_quote_hotel_room_hash
        )
      ],
      []
    )

    const hotelTickets = Object.values(hotels.searches).reduce<
      BudgetChartTicket[]
    >(
      (acc, cur) => [
        ...acc,
        ...cur.list.reduce((accList, curList) => {
          const foundRoom = curList.request_quote_hotel_rooms.filter(
            ({ request_quote_hotel_room_hash }) =>
              hotelFareHash.includes(request_quote_hotel_room_hash)
          )

          return [
            ...accList,
            ...foundRoom.reduce<BudgetChartTicket[]>((foundRoomAcc, room) => {
              const sameHash = hotelFareHash.filter(
                fareHash => fareHash === room.request_quote_hotel_room_hash
              ).length

              return [
                ...foundRoomAcc,
                ...Array(sameHash).fill({
                  standard_type: 'HOTEL',
                  total_value: room.total_value
                })
              ]
            }, [])
          ]
        }, [])
      ],
      []
    )

    const vehicleFareHash = request.full_request_vehicle.reduce<string[]>(
      (acc, { request_quote_vehicle_unique }) => [
        ...acc,
        ...request_quote_vehicle_unique
      ],
      []
    )

    const foundVehicleFare = vehicles.list.find(
      ({ request_quote_vehicle_unique }) =>
        vehicleFareHash.includes(request_quote_vehicle_unique)
    )

    const vehicleTickets: BudgetChartTicket[] = foundVehicleFare
      ? [
          {
            standard_type: 'VEHICLE',
            total_value: foundVehicleFare.total_value
          }
        ]
      : []

    if (airTickets.length) {
      for (let i = 0; i < airUsers; i += 1) {
        tickets.push(...airTickets)
      }
    }

    tickets.push(...hotelTickets)
    tickets.push(...vehicleTickets)

    return tickets
  }, [
    air.searches,
    hotels.searches,
    request.full_request_air,
    request.full_request_hotel,
    request.full_request_vehicle,
    request.request_id,
    request.request_user_data.length,
    vehicles.list
  ])

  useEffect(() => {
    let isSubscribed = true
    if (!policyRule?.enable_budget) {
      return () => {
        isSubscribed = false
      }
    }

    if (typeof request.request_id === 'string') {
      const budgets =
        request.request_standard_assignments.request_standard_sharings.reduce(
          requestStandardSharingsReduce,
          []
        )
      const { organization_id } = request
      const { policy_rule_id } = policyRule
      const tickets = getBudgetTicket()
      const budget_allocations = {
        active: true,
        budgets,
        organization_id,
        policy_rule_id,
        tickets
      }

      getBudgetAllocationsCheck(budget_allocations).then(response => {
        if (isSubscribed) {
          setBarCharts(response)
        }
      })
    } else {
      getBudgetAllocationsRequest(request.request_id).then(response => {
        if (isSubscribed) {
          setBarCharts(response)
        }
      })
    }

    return () => {
      isSubscribed = false
    }
  }, [getBudgetTicket, policyRule, request])

  if (!policyRule?.enable_budget) {
    return <></>
  }
  return (
    <StyledCard data-test-id="summary-budgets-card" title="common.budget.title">
      <Budget bars={barCharts} />
    </StyledCard>
  )
}

export {
  budgetTicketAirReduce,
  getBudgetAllocationsCheck,
  getBudgetAllocationsRequest,
  requestStandardSharingsReduce
}
export default BudgetSummary
